import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Crown, Gift, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function PaymentResult({ success }) {
  const [sp] = useSearchParams();
  const { refreshUser } = useAuth();
  const [state, setState] = useState(success ? "checking" : "cancelled");
  const started = useRef(false);

  useEffect(() => {
    if (!success || started.current) return;
    started.current = true;
    const id = sp.get("session_id");
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${id}`);
        if (data.payment_status === "paid") { await refreshUser(); return setState("paid"); }
        if (["expired", "failed"].includes(data.payment_status)) return setState("failed");
      } catch { /* keep polling */ }
      if (++tries >= 15) return setState("timeout");
      setTimeout(poll, 2000);
    };
    poll();
  }, [success, sp, refreshUser]);

  const isGift = sp.get("kind") === "gift";
  const view = {
    checking: { icon: <Loader2 className="w-10 h-10 animate-spin text-amber-500" />, title: "Confirming your payment...", text: "This takes just a moment." },
    paid: isGift
      ? { icon: <Gift className="w-10 h-10 text-amber-400" />, title: "Your gift is on its way", text: "We've emailed them a link to redeem their year of Pro, along with your note." }
      : { icon: <Crown className="w-10 h-10 text-amber-400" />, title: "Welcome to Pro", text: "Unlimited worlds are now yours to build." },
    failed: { icon: <XCircle className="w-10 h-10 text-rose-400" />, title: "Payment didn't go through", text: "No charge was made. You can try again anytime." },
    timeout: { icon: <Loader2 className="w-10 h-10 text-slate-500" />, title: "Still processing", text: "Your payment is being confirmed. Check your plan page in a minute." },
    cancelled: { icon: <XCircle className="w-10 h-10 text-slate-400" />, title: "Checkout cancelled", text: "No worries. Your free plan is still active." },
  }[state];

  return (
    <div className="relative z-10 min-h-screen flex items-center justify-center px-6" data-testid="payment-result-page">
      <div className="max-w-md text-center space-y-5 rise">
        <div className="flex justify-center">{view.icon}</div>
        <h1 className="font-display text-4xl sm:text-5xl text-amber-50" data-testid="payment-result-title">{view.title}</h1>
        <p className="text-slate-400">{view.text}</p>
        <div className="flex justify-center gap-3 pt-2">
          <Link to="/worlds" className="rounded-full bg-amber-500 hover:bg-amber-400 text-onaccent font-semibold px-6 py-2.5" data-testid="payment-go-worlds-btn">Go to my worlds</Link>
          <Link to={isGift ? "/gift" : "/billing"} className="rounded-full border border-line px-6 py-2.5 text-slate-200 hover:border-amber-500/40" data-testid="payment-go-billing-btn">{isGift ? "Gifts you've sent" : "View plan"}</Link>
        </div>
      </div>
    </div>
  );
}
